import { useEffect, useState } from 'react'

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform?: string }>
}

export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [available, setAvailable] = useState(false)
  const [lastChoice, setLastChoice] = useState<'accepted' | 'dismissed' | null>(null)
  const [isStandalone, setIsStandalone] = useState(false)

  useEffect(() => {
    const check = () =>
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true
    setIsStandalone(check())
    const mq = window.matchMedia('(display-mode: standalone)')
    const onChange = (e: MediaQueryListEvent) => setIsStandalone(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  // Development override: enable via ?forceInstall=1 or localStorage.forceInstall = '1'
  useEffect(() => {
    try {
      const url = typeof window !== 'undefined' ? new URL(window.location.href) : null
      const force = (url && url.searchParams.get('forceInstall') === '1') || (typeof localStorage !== 'undefined' && localStorage.getItem('forceInstall') === '1')
      if (force) {
        console.log('[usePwaInstall] forceInstall active (dev)')
        setAvailable(true)
      }
    } catch (err) {
      // ignore
    }
  }, [])

  useEffect(() => {
    function onBeforeInstallPrompt(e: Event) {
      console.log('[usePwaInstall] beforeinstallprompt event fired')
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      setAvailable(true)
    }

    function onAppInstalled() {
      console.log('[usePwaInstall] appinstalled event fired')
      setDeferredPrompt(null)
      setAvailable(false)
      setLastChoice('accepted')
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt as EventListener)
    window.addEventListener('appinstalled', onAppInstalled as EventListener)

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt as EventListener)
      window.removeEventListener('appinstalled', onAppInstalled as EventListener)
    }
  }, [])

  const promptInstall = async () => {
    if (!deferredPrompt) return null
    try {
      await deferredPrompt.prompt()
      const choice = await deferredPrompt.userChoice
      setLastChoice(choice.outcome)
      // clear the prompt after using it
      setDeferredPrompt(null)
      setAvailable(false)
      return choice.outcome
    } catch (err) {
      console.error('Error prompting install', err)
      return null
    }
  }

  return { available, promptInstall, lastChoice, isStandalone }
}
